/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CidModifierGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select-shadcn';
import Toggle from '../../../components/ui/Toggle';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';

interface CidNumberSettings {
  skip: number | null;
  length: number | null;
  prepend: string;
  append: string;
}

interface CidNameSettings {
  prepend: string;
  append: string;
  replaceWith: string;
  forceE164: boolean;
  stripNonDigits: boolean;
  blockNameIfEmpty: boolean;
}

interface DatabaseSettings {
  database: string;
  query: string;
  host: string;
  username: string;
  password: string;
}

interface HttpSettings {
  url: string;
  authUser: string;
  authPassword: string;
  timeout: number;
  failoverBehavior: 'keepOriginal' | 'replaceWithFallback' | 'keepOnlyNumber' | 'keepOnlyName' | 'rejectCall';
  retries: number;
}

interface FormData {
  name: string;
  source: 'static' | 'database' | 'http';
  enabled: boolean;
  staticSettings: { cidNumber: CidNumberSettings; cidName: CidNameSettings; };
  databaseSettings: DatabaseSettings;
  httpSettings: HttpSettings;
}

interface CidModifierGeneralSectionProps {
  formData: FormData;
  errors: Record<string, string>;
  handleChange: (field: string, value: string | number | boolean | null) => void;
  handleStaticSettingChange: (section: 'cidNumber' | 'cidName', field: string, value: string | number | boolean | null) => void;
  handleDatabaseSettingChange: (field: string, value: string | number | boolean | null) => void;
  handleHttpSettingChange: (field: string, value: string | number | boolean | null) => void;
  t: (key: string, fallback?: string) => string;
}

/** Reusable Name + Source row � shared by all three source-type layouts */
function NameAndSourceRow({
  formData, errors, handleChange, t,
}: Pick<CidModifierGeneralSectionProps, 'formData' | 'errors' | 'handleChange' | 't'>) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
      <Label htmlFor="name">
        <SimpleTooltip content={t('cidModifiers.fields.name.tooltip')}>
          <span>{t('cidModifiers.fields.name.label')}<span className="text-red-500 ml-1">*</span></span>
        </SimpleTooltip>
      </Label>
      <div>
        <Input id="name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
          placeholder={t('cidModifiers.fields.name.placeholder')}
          className={errors.name ? 'border-red-500' : ''} />
        {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
      </div>
      <Label htmlFor="source">
        <SimpleTooltip content={t('cidModifiers.fields.source.tooltip')}>
          <span>{t('cidModifiers.fields.source.label')}<span className="text-red-500 ml-1">*</span></span>
        </SimpleTooltip>
      </Label>
      <div>
        <Select value={formData.source} onValueChange={(v) => handleChange('source', v)}>
          <SelectTrigger><SelectValue placeholder={t('cidModifiers.fields.source.placeholder')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="static">{t('cidModifiers.sources.static')}</SelectItem>
            <SelectItem value="database">{t('cidModifiers.sources.database')}</SelectItem>
            <SelectItem value="http">{t('cidModifiers.sources.http')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/** Enabled toggle row � shared by all layouts */
function EnabledRow({
  enabled, onChange, t,
}: { enabled: boolean; onChange: (v: boolean) => void; t: (k: string) => string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
      <Label htmlFor="enabled">
        <SimpleTooltip content={t('cidModifiers.fields.enabled.tooltip')}>
          {t('cidModifiers.fields.enabled.label')}
        </SimpleTooltip>
      </Label>
      <div><Toggle id="enabled" value={enabled} onChange={onChange} /></div>
    </div>
  );
}

export function CidModifierGeneralSection({
  formData, errors, handleChange, handleStaticSettingChange,
  handleDatabaseSettingChange, handleHttpSettingChange, t,
}: CidModifierGeneralSectionProps) {

  /* -- Static Source -- */
  if (formData.source === 'static') {
    const { cidNumber, cidName } = formData.staticSettings;
    return (
      <div className="space-y-3">
        <NameAndSourceRow formData={formData} errors={errors} handleChange={handleChange} t={t} />

        {/* Force E.164 | Strip Non-Digit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="forceE164">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidName.forceE164.tooltip')}>
              {t('cidModifiers.staticSettings.cidName.forceE164.label')}
            </SimpleTooltip>
          </Label>
          <div><Toggle id="forceE164" value={cidName.forceE164} onChange={(v) => handleStaticSettingChange('cidName', 'forceE164', v)} /></div>
          <Label htmlFor="stripNonDigits">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidName.stripNonDigits.tooltip')}>
              {t('cidModifiers.staticSettings.cidName.stripNonDigits.label')}
            </SimpleTooltip>
          </Label>
          <div><Toggle id="stripNonDigits" value={cidName.stripNonDigits} onChange={(v) => handleStaticSettingChange('cidName', 'stripNonDigits', v)} /></div>
        </div>

        {/* Section headings */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <div className="col-span-2"><h3 className="text-sm font-semibold text-foreground">{t('cidModifiers.staticSettings.cidNumber.title')}</h3></div>
          <div className="col-span-2"><h3 className="text-sm font-semibold text-foreground">{t('cidModifiers.staticSettings.cidName.title')}</h3></div>
        </div>

        {/* Skip/Length | CID Name Prepend */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="cidNumberSkip">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidNumber.skip.tooltip')}>
              {t('cidModifiers.staticSettings.cidNumber.skip.label')} / {t('cidModifiers.staticSettings.cidNumber.length.label')}
            </SimpleTooltip>
          </Label>
          <div className="flex gap-2">
            <Input id="cidNumberSkip" type="number" value={cidNumber.skip ?? ''} onChange={(e) => handleStaticSettingChange('cidNumber', 'skip', e.target.value ? parseInt(e.target.value) : null)} placeholder="Skip" className="flex-1" />
            <Input id="cidNumberLength" type="number" value={cidNumber.length ?? ''} onChange={(e) => handleStaticSettingChange('cidNumber', 'length', e.target.value ? parseInt(e.target.value) : null)} placeholder="Length" className="flex-1" />
          </div>
          <Label htmlFor="cidNamePrepend">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidName.prepend.tooltip')}>
              {t('cidModifiers.staticSettings.cidName.prepend.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="cidNamePrepend" value={cidName.prepend} onChange={(e) => handleStaticSettingChange('cidName', 'prepend', e.target.value)} placeholder="" /></div>
        </div>

        {/* Prepend | Append */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="cidNumberPrepend">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidNumber.prepend.tooltip')}>
              {t('cidModifiers.staticSettings.cidNumber.prepend.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="cidNumberPrepend" value={cidNumber.prepend} onChange={(e) => handleStaticSettingChange('cidNumber', 'prepend', e.target.value)} placeholder="" /></div>
          <Label htmlFor="cidNameAppend">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidName.append.tooltip')}>
              {t('cidModifiers.staticSettings.cidName.append.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="cidNameAppend" value={cidName.append} onChange={(e) => handleStaticSettingChange('cidName', 'append', e.target.value)} placeholder="" /></div>
        </div>

        {/* Append | Replace With */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="cidNumberAppend">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidNumber.append.tooltip')}>
              {t('cidModifiers.staticSettings.cidNumber.append.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="cidNumberAppend" value={cidNumber.append} onChange={(e) => handleStaticSettingChange('cidNumber', 'append', e.target.value)} placeholder="" /></div>
          <Label htmlFor="cidNameReplaceWith">
            <SimpleTooltip content={t('cidModifiers.staticSettings.cidName.replaceWith.tooltip')}>
              {t('cidModifiers.staticSettings.cidName.replaceWith.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="cidNameReplaceWith" value={cidName.replaceWith} onChange={(e) => handleStaticSettingChange('cidName', 'replaceWith', e.target.value)} placeholder="" /></div>
        </div>

        <EnabledRow enabled={formData.enabled} onChange={(v) => handleChange('enabled', v)} t={t} />
      </div>
    );
  }

  /* -- Database Source -- */
  if (formData.source === 'database') {
    const db = formData.databaseSettings;
    return (
      <div className="space-y-3">
        <NameAndSourceRow formData={formData} errors={errors} handleChange={handleChange} t={t} />

        {/* Database | Host */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="dbDatabase">
            <SimpleTooltip content={t('cidModifiers.databaseSettings.database.tooltip')}>
              <span>{t('cidModifiers.databaseSettings.database.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div><Input id="dbDatabase" value={db.database} onChange={(e) => handleDatabaseSettingChange('database', e.target.value)} placeholder="database_name" /></div>
          <Label htmlFor="dbHost">
            <SimpleTooltip content={t('cidModifiers.databaseSettings.host.tooltip')}>
              <span>{t('cidModifiers.databaseSettings.host.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div><Input id="dbHost" value={db.host} onChange={(e) => handleDatabaseSettingChange('host', e.target.value)} placeholder="localhost or [::1] for IPv6" /></div>
        </div>

        {/* Query */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
          <Label htmlFor="dbQuery">
            <SimpleTooltip content={t('cidModifiers.databaseSettings.query.tooltip')}>
              <span>{t('cidModifiers.databaseSettings.query.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div className="col-span-3">
            <Textarea id="dbQuery" className="min-h-[100px]" value={db.query}
              onChange={(e) => handleDatabaseSettingChange('query', e.target.value)}
              placeholder="SELECT cidname, cidnumber FROM table WHERE number = ?" />
          </div>
        </div>

        {/* Username | Password */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="dbUsername">
            <SimpleTooltip content={t('cidModifiers.databaseSettings.username.tooltip')}>
              <span>{t('cidModifiers.databaseSettings.username.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div><Input id="dbUsername" value={db.username} onChange={(e) => handleDatabaseSettingChange('username', e.target.value)} placeholder="username" /></div>
          <Label htmlFor="dbPassword">
            <SimpleTooltip content={t('cidModifiers.databaseSettings.password.tooltip')}>
              {t('cidModifiers.databaseSettings.password.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="dbPassword" type="password" value={db.password} onChange={(e) => handleDatabaseSettingChange('password', e.target.value)} placeholder="password" /></div>
        </div>

        <EnabledRow enabled={formData.enabled} onChange={(v) => handleChange('enabled', v)} t={t} />
      </div>
    );
  }

  /* -- HTTP Source -- */
  if (formData.source === 'http') {
    const http = formData.httpSettings;
    return (
      <div className="space-y-3">
        <NameAndSourceRow formData={formData} errors={errors} handleChange={handleChange} t={t} />

        {/* URL */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="httpUrl">
            <SimpleTooltip content={t('cidModifiers.httpSettings.url.tooltip')}>
              <span>{t('cidModifiers.httpSettings.url.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div className="col-span-3">
            <Input id="httpUrl" type="url" value={http.url} onChange={(e) => handleHttpSettingChange('url', e.target.value)} placeholder="https://mycrm.com/cid.php?caller=[CIDNUM]&callername=[CIDNAME]" />
          </div>
        </div>

        {/* Auth User | Auth Password */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="httpAuthUser">
            <SimpleTooltip content={t('cidModifiers.httpSettings.authUser.tooltip')}>
              {t('cidModifiers.httpSettings.authUser.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="httpAuthUser" value={http.authUser} onChange={(e) => handleHttpSettingChange('authUser', e.target.value)} placeholder="username" /></div>
          <Label htmlFor="httpAuthPassword">
            <SimpleTooltip content={t('cidModifiers.httpSettings.authPassword.tooltip')}>
              {t('cidModifiers.httpSettings.authPassword.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="httpAuthPassword" type="password" value={http.authPassword} onChange={(e) => handleHttpSettingChange('authPassword', e.target.value)} placeholder="password" /></div>
        </div>

        {/* Timeout | Retries */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="httpTimeout">
            <SimpleTooltip content={t('cidModifiers.httpSettings.timeout.tooltip')}>
              {t('cidModifiers.httpSettings.timeout.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="httpTimeout" type="number" min="1" value={http.timeout} onChange={(e) => handleHttpSettingChange('timeout', parseInt(e.target.value) || 5)} placeholder="5" /></div>
          <Label htmlFor="httpRetries">
            <SimpleTooltip content={t('cidModifiers.httpSettings.retries.tooltip')}>
              {t('cidModifiers.httpSettings.retries.label')}
            </SimpleTooltip>
          </Label>
          <div><Input id="httpRetries" type="number" min="0" value={http.retries} onChange={(e) => handleHttpSettingChange('retries', parseInt(e.target.value) || 0)} placeholder="0" /></div>
        </div>

        {/* Failover Behavior */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Label htmlFor="httpFailoverBehavior">
            <SimpleTooltip content={t('cidModifiers.httpSettings.failoverBehavior.tooltip')}>
              {t('cidModifiers.httpSettings.failoverBehavior.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={http.failoverBehavior} onValueChange={(v) => handleHttpSettingChange('failoverBehavior', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="keepOriginal">{t('cidModifiers.httpSettings.failoverBehavior.options.keepOriginal')}</SelectItem>
                <SelectItem value="replaceWithFallback">{t('cidModifiers.httpSettings.failoverBehavior.options.replaceWithFallback')}</SelectItem>
                <SelectItem value="keepOnlyNumber">{t('cidModifiers.httpSettings.failoverBehavior.options.keepOnlyNumber')}</SelectItem>
                <SelectItem value="keepOnlyName">{t('cidModifiers.httpSettings.failoverBehavior.options.keepOnlyName')}</SelectItem>
                <SelectItem value="rejectCall">{t('cidModifiers.httpSettings.failoverBehavior.options.rejectCall')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <EnabledRow enabled={formData.enabled} onChange={(v) => handleChange('enabled', v)} t={t} />
      </div>
    );
  }

  /* -- Fallback (unknown source) -- */
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <NameAndSourceRow formData={formData} errors={errors} handleChange={handleChange} t={t} />
      </div>
      <EnabledRow enabled={formData.enabled} onChange={(v) => handleChange('enabled', v)} t={t} />
    </div>
  );
}
